import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Upload, CheckCircle, Clock, XCircle, FileText, Sparkles, Plus, Trash2, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../common/Toast';
import ProjectIntelligenceWorkspaceShell from './ProjectIntelligenceWorkspaceShell';
import { getPiWorkspacePath } from './projectIntelligencePhases';
import '../../styles/dpr-premium.css';

const getDefaultCompliances = (type) => {
  if (type === 'Water Infrastructure') {
    return [
      { clearanceName: 'MPCB Clearance', authority: 'Maharashtra Pollution Control Board', status: 'Pending', dueDate: '', documentUrl: null },
      { clearanceName: 'PWD Norms Compliance', authority: 'Public Works Department', status: 'Pending', dueDate: '', documentUrl: null },
      { clearanceName: 'CPHEEO Standards', authority: 'Ministry of Housing and Urban Affairs', status: 'Pending', dueDate: '', documentUrl: null },
      { clearanceName: 'Traffic NOC', authority: 'Traffic Police Department', status: 'Pending', dueDate: '', documentUrl: null },
    ];
  }
  return [
    { clearanceName: 'Environmental Clearance', authority: 'Ministry of Environment', status: 'Pending', dueDate: '', documentUrl: null },
    { clearanceName: 'Local Body NOC', authority: 'Municipal Corporation', status: 'Pending', dueDate: '', documentUrl: null },
  ];
};

export default function RegulatoryApprovals() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compliances, setCompliances] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);

  useEffect(() => {
    if (id) fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/sourcing/project-requirements/${id}`);
      if (res.data.success) {
        const p = res.data.data;
        setProject(p);
        if (p.compliances && p.compliances.length > 0) {
          setCompliances(p.compliances);
        } else {
          setCompliances(getDefaultCompliances(p.projectType || p.category));
        }
      } else {
        setProject(null);
      }
    } catch (err) {
      error('Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.put(`/sourcing/project-requirements/${id}/compliance`, { compliances });
      if (res.data.success) {
        success('Compliance matrix saved successfully');
      }
    } catch (err) {
      error('Failed to save compliance data');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!project) return;
    setGenerating(true);
    try {
      const prompt = `
        You are an expert project compliance consultant.
        Project Title: ${project.projectTitle}
        Type: ${project.projectType || project.category}
        Location: ${project.location}
        Description: ${project.problemStatement}
        
        CRITICAL INSTRUCTION: You must provide a HIGHLY SPECIFIC, exhaustive list of the exact regulatory and departmental clearances required for this specific project type in this specific location. DO NOT provide generic placeholders.
        
        Return STRICTLY a JSON array of objects with exactly these keys:
        - "clearanceName": string (e.g. "SEBI Approval", "MPCB NOC", "Data Privacy Impact Assessment")
        - "authority": string (e.g. "Reserve Bank of India", "Ministry of Environment")
        - "status": string (always use "Pending")
        - "dueDate": string (leave as "")
        - "documentUrl": string (leave as "")
      `;

      const res = await api.post('/ai/gemini', { message: prompt });

      const aiText = res.data.response || res.data.data;
      if (aiText) {
        const clean = aiText.replace(/```json/gi, '').replace(/```/g, '').trim();
        const parsed = JSON.parse(clean);
        if (Array.isArray(parsed)) {
          setCompliances(parsed);
          success('AI drafted compliance matrix');
          await api.put(`/sourcing/project-requirements/${id}/compliance`, { compliances: parsed });
        }
      } else {
        error('AI Generation returned invalid format');
      }
    } catch (err) {
      error('AI Generation Failed');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;
    try {
      setUploadingIndex(index);
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post(`/sourcing/project-requirements/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        const docs = res.data.data;
        const latestDoc = docs[docs.length - 1];
        updateCompliance(index, 'documentUrl', latestDoc.localPath);
        success('Document uploaded and attached');
      }
    } catch (err) {
      error('Failed to upload document');
    } finally {
      setUploadingIndex(null);
    }
  };

  const updateCompliance = (index, field, value) => {
    const newArr = [...compliances];
    newArr[index][field] = value;
    setCompliances(newArr);
  };

  const addRow = () => {
    setCompliances([...compliances, { clearanceName: '', authority: '', status: 'Pending', dueDate: '', documentUrl: null }]);
  };

  const removeRow = (index) => {
    setCompliances(compliances.filter((_, i) => i !== index));
  };

  const obtained = compliances.filter(c => c.status === 'Obtained').length;
  const pending = compliances.filter(c => c.status === 'Pending').length;
  const na = compliances.filter(c => c.status === 'Not Applicable').length;

  const headerActions = project
    ? [
        {
          label: saving ? 'Saving...' : 'Save',
          icon: <Save size={16} />,
          variant: 'outline',
          onClick: handleSave,
          disabled: saving,
        },
        {
          label: 'Proceed to Finalisation',
          icon: <ChevronRight size={16} />,
          variant: 'primary',
          onClick: () => navigate(getPiWorkspacePath('finalisation', id)),
        },
      ]
    : [];

  return (
    <ProjectIntelligenceWorkspaceShell
      phase="regulatory"
      projectId={id}
      projectTitle={project?.projectTitle}
      loading={loading}
      notFound={!loading && !project}
      actions={headerActions}
    >
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="pi-stat-card d-flex align-items-center gap-3">
            <div className="rounded-circle bg-success-subtle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
              <CheckCircle size={22} className="text-success" />
            </div>
            <div>
              <div className="text-muted small text-uppercase">Obtained</div>
              <div className="fw-bold fs-4">{obtained}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="pi-stat-card d-flex align-items-center gap-3">
            <div className="rounded-circle bg-warning-subtle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
              <Clock size={22} className="text-warning" />
            </div>
            <div>
              <div className="text-muted small text-uppercase">Pending</div>
              <div className="fw-bold fs-4">{pending}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="pi-stat-card d-flex align-items-center gap-3">
            <div className="rounded-circle bg-secondary-subtle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
              <XCircle size={22} className="text-secondary" />
            </div>
            <div>
              <div className="text-muted small text-uppercase">Not Applicable</div>
              <div className="fw-bold fs-4">{na}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="pi-alert-banner pi-alert-banner--info mb-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div className="d-flex align-items-center gap-3">
          <Sparkles size={22} className="text-primary" />
          <div>
            <div className="fw-semibold">AI compliance scanner</div>
            <div className="small text-muted">Auto-detect regulatory requirements based on project type and location.</div>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={handleGenerateAI}
          disabled={generating}
        >
          <Sparkles size={16} /> {generating ? 'Scanning...' : 'Generate Checklist'}
        </button>
      </div>

      <div className="d-flex flex-column gap-3">
        {compliances.map((item, i) => (
          <div key={i} className="pi-card overflow-hidden">
            <div className="d-flex align-items-stretch">
              <div
                style={{
                  width: 5,
                  flexShrink: 0,
                  background:
                    item.status === 'Obtained'
                      ? '#10b981'
                      : item.status === 'Not Applicable'
                        ? '#94a3b8'
                        : '#f59e0b',
                }}
              />
              <div className="flex-grow-1 p-3 px-4">
                <div className="row align-items-center g-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 fw-semibold p-0 bg-transparent"
                      value={item.clearanceName || item.name || ''}
                      onChange={(e) => updateCompliance(i, 'clearanceName', e.target.value)}
                      placeholder="Clearance name"
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 text-muted p-0 bg-transparent mt-1"
                      value={item.authority || ''}
                      onChange={(e) => updateCompliance(i, 'authority', e.target.value)}
                      placeholder="Responsible authority"
                    />
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select form-select-sm"
                      value={item.status}
                      onChange={(e) => updateCompliance(i, 'status', e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Obtained">Obtained</option>
                      <option value="Not Applicable">N/A</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={item.dueDate ? item.dueDate.split('T')[0] : ''}
                      onChange={(e) => updateCompliance(i, 'dueDate', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    {item.documentUrl ? (
                      <div className="d-flex align-items-center gap-2">
                        <span className="pi-badge pi-badge--success">
                          <FileText size={12} /> Attached
                        </span>
                        <label className="btn btn-sm btn-light m-0">
                          <Upload size={12} />
                          <input type="file" hidden onChange={(e) => handleFileUpload(i, e.target.files[0])} disabled={uploadingIndex === i} />
                        </label>
                      </div>
                    ) : (
                      <label className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2 m-0">
                        <Upload size={14} /> {uploadingIndex === i ? 'Uploading...' : 'Upload proof'}
                        <input type="file" hidden onChange={(e) => handleFileUpload(i, e.target.files[0])} disabled={uploadingIndex === i} />
                      </label>
                    )}
                  </div>
                  <div className="col-md-1 text-end">
                    <button type="button" className="btn btn-sm btn-light" onClick={() => removeRow(i)} aria-label="Remove">
                      <Trash2 size={15} className="text-muted" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-light border border-dashed d-flex align-items-center justify-content-center gap-2 py-3 rounded-3 text-muted"
          onClick={addRow}
        >
          <Plus size={18} /> Add compliance item
        </button>
      </div>
    </ProjectIntelligenceWorkspaceShell>
  );
}
